import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, Send, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { getWhatsAppFlows, sendWhatsAppFlow } from '../../services/api/implementation/impl';

function resolveFlowApprovalStatus(flow) {
  return String(
    flow?.template_approval_status ?? flow?.template_status ?? flow?.approval_status ?? ''
  ).toUpperCase();
}

// A flow can only be sent once its opener template is approved by Meta.
function isFlowSendable(flow) {
  const status = resolveFlowApprovalStatus(flow);
  return status === 'APPROVED' || status === 'ACTIVE';
}

/**
 * Modal that lets the user pick a project's sendable WhatsApp flow from a
 * dropdown and send it to many leads at once via a single sendWhatsAppFlow
 * call (lead_ids array).
 */
const BulkSendFlowModal = ({ projectId, leadIds = [], onClose, onSent }) => {
  const [selectedFlowId, setSelectedFlowId] = useState('');

  const {
    data: flows = [],
    isLoading,
    isError,
    refetch
  } = useQuery({
    queryKey: ['whatsappFlows', projectId],
    queryFn: () => getWhatsAppFlows(projectId),
    enabled: Boolean(projectId)
  });

  const sendableFlows = useMemo(() => flows.filter(isFlowSendable), [flows]);

  const selectedFlow = useMemo(
    () => sendableFlows.find((flow) => String(flow.id) === String(selectedFlowId)) || null,
    [sendableFlows, selectedFlowId]
  );

  const flowMutation = useMutation({
    mutationFn: (flowId) =>
      sendWhatsAppFlow(flowId, {
        recipient_type: 'lead',
        lead_ids: leadIds,
        variables: {},
        skip_template_if_session_open: false
      })
  });

  const handleSend = () => {
    if (flowMutation.isPending || leadIds.length === 0) return;
    if (!selectedFlow) {
      toast.error('Please select a flow to send.');
      return;
    }
    flowMutation.mutate(selectedFlow.id, {
      onSuccess: () => {
        toast.success(
          `“${selectedFlow.name || 'Flow'}” sent to ${leadIds.length} lead${
            leadIds.length > 1 ? 's' : ''
          }.`
        );
        onSent?.();
        onClose?.();
      },
      onError: (err) => {
        const message =
          err?.response?.data?.message || 'Could not send flow. Please try again.';
        toast.error(message);
      }
    });
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900">Send a flow</p>
            <p className="truncate text-xs text-gray-500">
              To {leadIds.length} selected lead{leadIds.length > 1 ? 's' : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={flowMutation.isPending}
            className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
            aria-label="Close">
            <X className="h-4 w-4" strokeWidth={2.25} />
          </button>
        </div>

        <div className="p-4">
          {!projectId ? (
            <p className="py-8 text-center text-sm text-gray-500">
              These leads aren’t linked to a single project, so flows can’t be listed.
            </p>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center gap-2 py-10 text-center">
              <p className="text-sm text-gray-600">Couldn’t load flows.</p>
              <button
                type="button"
                onClick={() => refetch()}
                className="rounded-lg bg-[#2A565E] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2A565E]/90">
                Try again
              </button>
            </div>
          ) : sendableFlows.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">
              No approved flows available for this project. Only flows whose opener template is
              approved by Meta can be sent.
            </p>
          ) : (
            <>
              <label className="mb-1.5 block text-xs font-semibold text-gray-600">
                Select a flow
              </label>
              <select
                value={selectedFlowId}
                onChange={(e) => setSelectedFlowId(e.target.value)}
                disabled={flowMutation.isPending}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition-colors focus:border-[#2A565E] focus:ring-1 focus:ring-[#2A565E] disabled:opacity-50">
                <option value="" disabled>
                  Choose a flow…
                </option>
                {sendableFlows.map((flow) => (
                  <option key={flow.id} value={flow.id}>
                    {flow.name || 'Untitled flow'}
                  </option>
                ))}
              </select>

              {selectedFlow?.description ? (
                <p className="mt-2 text-xs text-gray-400">{selectedFlow.description}</p>
              ) : null}

              <div className="mt-5 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={flowMutation.isPending}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-gray-500 transition-colors hover:text-gray-700 disabled:opacity-50">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={flowMutation.isPending || !selectedFlow}
                  className="flex items-center gap-2 rounded-lg bg-[#2A565E] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#2A565E]/90 disabled:cursor-not-allowed disabled:opacity-50">
                  {flowMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" strokeWidth={2} />
                  )}
                  Send
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkSendFlowModal;
